// Признак «создание PR начато, номер не записан, и дописать его больше
// некому».
//
// Наивное «отметка есть, номера нет» признаком осиротевшего PR НЕ
// является: та же пара истинна всё время, пока идёт совершенно
// нормальное создание PR (отметка коммитится фазой 1 до обращения к
// хостингу, номер пишет фаза 3 — после ответа), и остаётся истинной
// навсегда у задачи, отменённой человеком (ADR-0058 § 3 — отметка не
// снимается никогда, снимающего пути в коде нет вовсе). Голая пара
// загоралась бы на каждой здоровой задаче в момент создания PR и не
// гасла бы на отменённых.
//
// Различает состояния не время, а то, **сделает ли автоматика ещё одну
// попытку записать номер**. Цепочка проверена по коду:
//
// - Отметку ставит только задача в `in_progress`: гейт
//   `abort(FAILED_PRECONDITION)` на любом другом статусе стоит выше
//   записи отметки (`backend/modules/task/grpc/pr_phases.py`).
// - Номер пишет единственный переход `in_progress → ci_running`
//   (`backend/modules/task/service/lifecycle.py::mark_ci_running`), и
//   он тоже требует `in_progress`. Второй путь записи,
//   `finalize_idempotent_hit`, идёт через тот же переход.
// - Провал между этими двумя точками не заканчивает историю. Страховка
//   истечения аренды (`backend/modules/task/lease/expiry.py`) возвращает
//   задачу в очередь — `mark_recovery_requeue`
//   (`backend/modules/task/service/escalation.py`), ребро
//   `in_progress | draft → queued`. Это НОВАЯ попытка ТОЙ ЖЕ жизни
//   задачи: план и ветка те же (`dispatch/retry_context.py` читает это
//   ребро именно так), поэтому повторное создание PR упирается в
//   конфликт по той же head-ветке и через
//   `resolve_conflicting_pull_request` дописывает номер уже созданному
//   pull request'у. Пока цикл «кодер → очередь → кодер» продолжается,
//   система лечится сама — показывать оператору нечего.
// - Цикл конечен: бюджет `recovery_count` (anti-thrash cap), и на его
//   исчерпании та же страховка зовёт `mark_recovery_escalated` →
//   `escalated`. Это единственная точка, где автоматика отступается:
//   новых попыток записать номер не будет, и случай становится делом
//   оператора.
//
// Отсюда признак: **отметка есть, номера нет, задача в `escalated`.**
// Не «не в рабочем статусе»: `queued`/`draft`/`planned` — это как раз
// окно, в котором восстановление ещё идёт, и бейдж там был бы ложной
// тревогой, зовущей оператора руками искать ветку ровно тогда, когда
// автоматика сама её подберёт.
//
// Терминальные `done`/`cancelled` признака не показывают — этого
// требует постановка задачи, и `done` с отметкой без номера недостижим
// (путь в `done` идёт через `ci_running`, где номер уже записан).
//
// Слепые зоны названы честно, обе — «признак не загорится», не «загорится
// зря»:
//
// - Задача, чья очередь активна на ДРУГОЙ задаче, уходит в
//   `_expire_lease` веткой `release_only`: аренда снимается, статус
//   остаётся `in_progress` навсегда. Автоматика её больше не берёт
//   (активация выбирает только `queued`), но и признак не загорится.
//   Ветка помечена в коде как defensive-гонка.
// - Задача в `queued` при остановленной очереди ждёт активации и бюджет
//   не тратит — до эскалации не доедет, пока очередь стоит.
//
// Что признак НЕ обещает: не «осиротевший PR существует», а «создание
// начато, номер не записан; PR мог быть создан на хостинге». Неответ
// хостинга и потерянный ответ неотличимы (ADR-0058 § 3) — подписи,
// использующие этот признак, обязаны сохранять эту неопределённость.

import type { TaskStatus } from "./api";

export interface PrAttemptView {
  readonly status: TaskStatus;
  readonly pr_number: number | null;
  readonly pr_creation_started_at: string | null;
}

// Единственный статус, в котором автоматика отступилась от попыток
// записать номер (см. докстринг модуля). Во всех остальных задача либо
// ещё создаёт PR, либо ведётся к следующей попытке, либо терминальна.
const AUTOMATION_GAVE_UP_STATUS: TaskStatus = "escalated";

/**
 * Создание pull request было начато, номер не записан, и новой попытки
 * его записать автоматика уже не сделает.
 */
export function hasUnrecordedPrAttempt(task: PrAttemptView): boolean {
  return (
    task.pr_creation_started_at !== null &&
    task.pr_number === null &&
    task.status === AUTOMATION_GAVE_UP_STATUS
  );
}
